import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { ShowToast } from "../../components/toast";

type InternalCategory = "fnb" | "retail" | "other";

export type RevenueCategoryEntry = {
  sourceKind: "service";
  sourceId: string;
  sourceName: string;
  suggestedInternalCategory: InternalCategory;
  currentInternalCategory: InternalCategory | null;
};

export type RevenueCategoryDiscoverResponse = {
  pmsType: string;
  entries: RevenueCategoryEntry[];
};

export type SaveRevenueCategoryEntry = {
  sourceKind: "service";
  sourceId: string;
  sourceName: string;
  internalCategory: InternalCategory;
};

export function useDiscoverRevenueCategories(userId: string | undefined) {
  return useQuery<RevenueCategoryDiscoverResponse>({
    queryKey: ["pmsRevenueCategories", userId, "discover"],
    queryFn: () => api.pmsRevenueCategories.discover(userId as string),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: unknown) => {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404) return false;
      return failureCount < 2;
    },
  });
}

export function useSaveRevenueCategoryMapping() {
  const queryClient = useQueryClient();
  return useMutation<
    { saved: number; pmsType: string },
    Error,
    { userId: string; entries: SaveRevenueCategoryEntry[] }
  >({
    mutationFn: ({ userId, entries }) =>
      api.pmsRevenueCategories.saveMapping(userId, entries),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({
        queryKey: ["pmsRevenueCategories", userId, "discover"],
      });
    },
    onError: () => {
      ShowToast({
        type: "error",
        message: "Failed to save revenue categories. Please try again.",
      });
    },
  });
}
